import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import { AuthenticationProvider } from '@zenbusiness/application-commons-authentication';
import { useEnv } from '@zenbusiness/application-commons-env';
import { GraphQLProvider } from '@zenbusiness/application-commons-graphql';
import Orphanage from '@zenbusiness/application-commons-hooks/Orphanage';
import { IdentityProvider } from '@zenbusiness/application-commons-identity';
import { IntercomProvider } from '@zenbusiness/application-commons-intercom';
import { IntlProvider } from '@zenbusiness/application-commons-intl';
import { LoadingZone, SuspenseZone } from '@zenbusiness/application-commons-loading';
import { LoggingProvider, ErrorBoundary } from '@zenbusiness/application-commons-logging';
import { VisitProvider } from '@zenbusiness/application-commons-visit';
import { WebView } from '@zenbusiness/application-commons-webview';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { ZuiThemeProvider } from '@zenbusiness/zui/styles';

import globalGraphQLErrorHandler from '#client/App/GraphQLErrorHandler';
import { DEFAULT_SEGMENT, NAME, RELEASE_VERSION } from '#client/build-env';
import { intlFormats } from '#client/constants';

import ClickstreamWrapper from './ClickstreamWrapper';

const emotionCache = createCache({
  key: NAME
});

// eslint-disable-next-line react/prop-types
export default function AppWrapper({ children, federated }) {
  const {
    AUTH0_AUDIENCE,
    AUTH0_CLIENTID,
    AUTH0_CONNECTION,
    AUTH0_DOMAIN,
    GRAPHQL_ROUTE,
    AUTH_PATHS
  } = useEnv('head-first'); // use MFE values
  const { INTERCOM_APPID, LOCALE } = useEnv('root-first'); // use parent values

  return (
    <LoadingZone fallback={<PageLoading />} loadId="App/AppWrapper">
      <LoggingProvider
        tag-frontend={NAME}
        tag-frontend-release={`${NAME}-${RELEASE_VERSION || 'unset'}`}
        tag-isWebView={String(WebView?.isWebView)}
      >
        <ZuiThemeProvider baseline={!federated} scopedBaseline={federated}>
          <ErrorBoundary fallback={<PageError />} id={`${NAME}/App/AppWrapper`}>
            <Orphanage>
              <CacheProvider value={emotionCache}>
                <SuspenseZone loadId={`${NAME}/App/AppWrapper#Intl`}>
                  <IntlProvider
                    defaultFormats={intlFormats}
                    defaultLocale="en-US"
                    findPath={`${DEFAULT_SEGMENT}/messages/en-US/${RELEASE_VERSION}`}
                    formats={intlFormats}
                    locale={LOCALE}
                  >
                    <AuthenticationProvider
                      audience={AUTH0_AUDIENCE}
                      clientID={AUTH0_CLIENTID}
                      connection={AUTH0_CONNECTION}
                      domain={AUTH0_DOMAIN}
                      {...AUTH_PATHS}
                      debug
                    >
                      <GraphQLProvider url={GRAPHQL_ROUTE} onError={globalGraphQLErrorHandler}>
                        <VisitProvider>
                          <IdentityProvider>
                            <ClickstreamWrapper>
                              <IntercomProvider appId={INTERCOM_APPID} hideDefaultLauncher>
                                {children}
                              </IntercomProvider>
                            </ClickstreamWrapper>
                          </IdentityProvider>
                        </VisitProvider>
                      </GraphQLProvider>
                    </AuthenticationProvider>
                  </IntlProvider>
                </SuspenseZone>
              </CacheProvider>
            </Orphanage>
          </ErrorBoundary>
        </ZuiThemeProvider>
      </LoggingProvider>
    </LoadingZone>
  );
}
